import React, { lazy } from 'react';

import { type Route, type Routes, useRouter } from 'react-resource-router';

import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';

import type { QueryBuilderView } from '../../pages/query-builder/types';
import { componentWithSuspense } from '../../utils';
import { AppAccessGate } from '../AppAccessGate';

const QueryBuilderPage = lazy(
	() =>
		import(
			/* webpackChunkName: "@atlaskit-internal_dtac-query-builder" */ '../../pages/query-builder'
		),
);

export const queryBuilderViewByRouteName: Record<string, QueryBuilderView> = {
	'query-builder-home': 'builder',
	'query-builder-saved-queries': 'saved-queries',
	'query-builder-saved-query-details': 'saved-query-details',
	'query-builder-saved-query-version': 'saved-query-details',
	'query-builder-saved-query-edit': 'saved-query-edit',
	'query-builder-saved-query-test': 'saved-query-test',
};

const QueryBuilderPageWithAccess = (): JSX.Element => {
	const [{ route }] = useRouter();
	const view = queryBuilderViewByRouteName[route.name] ?? 'builder';

	return (
		<AppAccessGate appName="Query Builder">
			<AnalyticsContext
				key={view}
				data={{ source: 'queryBuilder', attributes: { page: 'queryBuilder', location: view } }}
			>
				<QueryBuilderPage view={view} />
			</AnalyticsContext>
		</AppAccessGate>
	);
};

const queryBuilderRouteComponent = componentWithSuspense(QueryBuilderPageWithAccess);

const ROUTE_PREFIX = '/query-builder' as const;

const queryBuilderRoutesObject: Record<string, Route> = {
	home: {
		name: 'query-builder-home',
		path: ROUTE_PREFIX,
		exact: true,
		component: queryBuilderRouteComponent,
	},
	savedQueries: {
		name: 'query-builder-saved-queries',
		path: `${ROUTE_PREFIX}/saved-queries` as const,
		exact: true,
		component: queryBuilderRouteComponent,
	},
	savedQueryDetails: {
		name: 'query-builder-saved-query-details',
		path: `${ROUTE_PREFIX}/saved-queries/:queryKey` as const,
		exact: true,
		component: queryBuilderRouteComponent,
	},
	savedQueryVersion: {
		name: 'query-builder-saved-query-version',
		path: `${ROUTE_PREFIX}/saved-queries/:queryKey/versions/:version` as const,
		exact: true,
		component: queryBuilderRouteComponent,
	},
	savedQueryEdit: {
		name: 'query-builder-saved-query-edit',
		path: `${ROUTE_PREFIX}/saved-queries/:queryKey/edit` as const,
		exact: true,
		component: queryBuilderRouteComponent,
	},
	savedQueryTest: {
		name: 'query-builder-saved-query-test',
		path: `${ROUTE_PREFIX}/saved-queries/:queryKey/versions/:version/test` as const,
		exact: true,
		component: queryBuilderRouteComponent,
	},
} as const;

export const queryBuilderRoutes: Routes = Object.values(queryBuilderRoutesObject);
